import React, { useEffect, useState } from "react";
import { Tabs, Tab, Box, IconButton, Popover, Typography } from "@mui/material";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "./Photos.css";
import { fetchWithAuth } from "../../utils/fetchWithAuth";
import { useAuth } from "../../contexts/AuthContext";
import SponsoredSlot from "../Sponsored/SponsoredSlot";

const QR_DOWNLOAD_LIMIT = 10;

const normalizeImages = (items) =>
  (Array.isArray(items) ? items : [])
    .filter(Boolean)
    .map((item) => {
      const id = item.id ?? item.image_id ?? item.photo_id ?? item.uuid;
      return {
        ...item,
        id,
        favorite: Boolean(item.favorite),
      };
    });

function Photos() {
  const [activeTab, setActiveTab] = useState(0);
  const [images, setImages] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [favoritesLoading, setFavoritesLoading] = useState(false);
  const [favoritesError, setFavoritesError] = useState(null);
  const [favoritesLoaded, setFavoritesLoaded] = useState(false);
  const [albumDownloading, setAlbumDownloading] = useState(false);
  const [qrDownloading, setQrDownloading] = useState(false);
  const [infoAnchorEl, setInfoAnchorEl] = useState(null);
  const { authPayload, weddingId } = useAuth();
  const API = process.env.REACT_APP_API_BASE;

  const uploadPhotosUrl = weddingId
    ? `${window.location.origin}/upload-photos?wedding_id=${encodeURIComponent(
        weddingId
      )}`
    : null;

  const infoOpen = Boolean(infoAnchorEl);

  useEffect(() => {
    let isMounted = true;

    const loadImages = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetchWithAuth(`${API}/guest-qr-list-images`, {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        });

        if (!response || !response.ok) {
          throw new Error("Неуспешно вчитување на фотографиите.");
        }

        const data = await response.json();
        if (!isMounted) {
          return;
        }

        if (Array.isArray(data)) {
          setImages(normalizeImages(data));
        } else if (Array.isArray(data?.images)) {
          setImages(normalizeImages(data.images));
        } else {
          setImages([]);
        }
      } catch (err) {
        console.error("Error loading photos:", err);
        if (isMounted) {
          setError("Настана проблем при вчитување на фотографиите.");
          setImages([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (!authPayload) {
      setLoading(false);
      setImages([]);
      return () => {
        isMounted = false;
      };
    }

    loadImages();

    return () => {
      isMounted = false;
    };
  }, [API, authPayload]);

  useEffect(() => {
    if (activeTab !== 1 || !authPayload || favoritesLoaded) {
      return;
    }

    let isMounted = true;

    const loadFavorites = async () => {
      try {
        setFavoritesLoading(true);
        setFavoritesError(null);

        const response = await fetchWithAuth(`${API}/my-album`, {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        });

        if (!response || !response.ok) {
          throw new Error("Неуспешно вчитување на омилените фотографии.");
        }

        const data = await response.json();
        if (!isMounted) {
          return;
        }

        if (Array.isArray(data)) {
          setFavorites(normalizeImages(data));
        } else if (Array.isArray(data?.images)) {
          setFavorites(normalizeImages(data.images));
        } else {
          setFavorites([]);
        }
        setFavoritesLoaded(true);
      } catch (err) {
        console.error("Error loading favorite photos:", err);
        if (isMounted) {
          setFavoritesError("Настана проблем при вчитување на омилените фотографии.");
          setFavorites([]);
        }
      } finally {
        if (isMounted) {
          setFavoritesLoading(false);
        }
      }
    };

    loadFavorites();

    return () => {
      isMounted = false;
    };
  }, [API, activeTab, authPayload, favoritesLoaded]);

  const toggleFavorite = async (imageId, favorite) => {
    try {
      setError(null);
      setFavoritesError(null);
      const updatedFavoriteState = !Boolean(favorite);

      const response = await fetchWithAuth(
        `${API}/favorite-image?image_id=${imageId}&favorite=${updatedFavoriteState}`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
          },
        }
      );

      if (!response || !response.ok) {
        throw new Error("Неуспешно ажурирање на омилена фотографија.");
      }

      setImages((prevImages) =>
        prevImages.map((img) =>
          img.id === imageId ? { ...img, favorite: updatedFavoriteState } : img
        )
      );

      setFavorites((prevFavorites) => {
        const baseImage =
          images.find((img) => img.id === imageId) ||
          prevFavorites.find((img) => img.id === imageId);

        if (!baseImage) {
          return prevFavorites;
        }

        if (updatedFavoriteState) {
          const normalizedBase = { ...baseImage, favorite: true };
          const alreadyExists = prevFavorites.some((img) => img.id === imageId);
          if (alreadyExists) {
            return prevFavorites.map((img) =>
              img.id === imageId ? normalizedBase : img
            );
          }
          return [...prevFavorites, normalizedBase];
        }

        return prevFavorites.filter((img) => img.id !== imageId);
      });
    } catch (err) {
      console.error("Error updating favorite status:", err);
      if (activeTab === 1) {
        setFavoritesError("Неуспешно ажурирање на омилената фотографија.");
      } else {
        setError("Неуспешно ажурирање на омилената фотографија.");
      }
    }
  };

  const downloadFavoritesZip = async () => {
    if (!favorites || favorites.length === 0) return;
    try {
      setAlbumDownloading(true);
      const JSZip = await ensureJSZip();
      const zip = new JSZip();
      const folder = zip.folder('my-album');
      const tasks = favorites
        .filter((img) => !!img.url)
        .map(async (img, idx) => {
          const url = img.url;
          const filenameFromUrl = (url.split('/').pop() || `image_${idx + 1}`).split('?')[0];
          try {
            const res = await fetch(url, { mode: 'cors' });
            if (!res.ok) throw new Error('HTTP ' + res.status);
            const blob = await res.blob();
            folder.file(filenameFromUrl || `image_${idx + 1}.jpg`, blob);
          } catch (e) {
            // Skip files that fail (CORS or network); leave a note file
            folder.file(`_failed_${idx + 1}.txt`, `Неуспешно преземање за: ${url}\n${String(e)}`);
          }
        });
      await Promise.all(tasks);
      const out = await zip.generateAsync({ type: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(out);
      a.download = 'moj-album.zip';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        URL.revokeObjectURL(a.href);
        a.remove();
      }, 0);
    } catch (err) {
      console.error('ZIP error:', err);
      alert('Неуспешно креирање ZIP. Можно е CORS ограничување од CDN.');
    } finally {
      setAlbumDownloading(false);
    }
  };

  const downloadQrPng = async () => {
    if (!uploadPhotosUrl || !weddingId) {
      alert("Недостига wedding_id за генерирање QR код.");
      return;
    }

    const attemptsStorageKey = `qr_download_attempts_${weddingId}`;
    const currentAttempts = Number(localStorage.getItem(attemptsStorageKey) || "0");
    if (currentAttempts >= QR_DOWNLOAD_LIMIT) {
      alert("Го достигнавте лимитот за преземање QR код (10 обиди).");
      return;
    }

    try {
      setQrDownloading(true);
      localStorage.setItem(attemptsStorageKey, String(currentAttempts + 1));

      const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=1024x1024&format=png&data=${encodeURIComponent(
        uploadPhotosUrl
      )}`;
      const response = await fetch(qrUrl);
      if (!response.ok) {
        throw new Error("Неуспешно преземање на QR код.");
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `wedding-${weddingId}-upload-photos-qr.png`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        URL.revokeObjectURL(objectUrl);
        a.remove();
      }, 0);
    } catch (err) {
      console.error("QR download error:", err);
      alert("Неуспешно преземање на QR кодот.");
    } finally {
      setQrDownloading(false);
    }
  };

  const renderGallery = (photoList, emptyMessage, showQrButton = false) => {
    if (!photoList.length) {
      return (
        <div className="photo-empty-state">
          <p>{emptyMessage}</p>
          {showQrButton && (
            <button
              type="button"
              className="photos-upload-link"
              onClick={downloadQrPng}
              disabled={!weddingId || qrDownloading}
            >
              {qrDownloading ? "Се презема QR..." : "Преземи QR код (PNG)"}
            </button>
          )}
        </div>
      );
    }

    return (
      <div className="photo-gallery">
        {photoList.map((image, index) => {
          const imageId = image.id ?? image.image_id;

          return (
            <div
              key={imageId ?? `${image.url}-${index}`}
              className="photo-card"
            >
              <img
                src={image.url}
                alt={
                  image.description ?? `Wedding photo ${imageId ?? index + 1}`
                }
                loading="lazy"
              />
              {imageId && (
                <button
                  type="button"
                  className={`favorite-button ${
                    image.favorite ? "is-active" : ""
                  }`}
                  onClick={() => toggleFavorite(imageId, image.favorite)}
                  aria-label={
                    image.favorite
                      ? "Отстрани од омилени"
                      : "Додај во омилени"
                  }
                >
                  {image.favorite ? (
                    <FavoriteIcon fontSize="small" />
                  ) : (
                    <FavoriteBorderIcon fontSize="small" />
                  )}
                </button>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderAllPhotos = () => {
    if (loading) {
      return <p className="photo-status">Се вчитуваат фотографиите...</p>;
    }

    if (error) {
      return <p className="photo-status error">{error}</p>;
    }

    if (!images.length) {
      return renderGallery(
        [],
        "Немате додадено фотографии.",
        true
      );
    }

    const sliderImages = images.filter((image) => Boolean(image.url));

    return (
      <>
        {sliderImages.length > 0 && (
          <div className="photo-swiper-wrapper">
            <Swiper
              className="photo-swiper"
              modules={[Navigation, Pagination]}
              navigation
              pagination={{ clickable: true }}
              spaceBetween={24}
              slidesPerView={1}
            >
              {sliderImages.map((image, index) => {
                const imageId = image.id ?? image.image_id;

                return (
                  <SwiperSlide key={imageId ?? `${image.url}-${index}`}>
                    <div className="photo-swiper-slide">
                      <img
                        src={image.url}
                        alt={
                          image.description ??
                          `Wedding photo ${imageId ?? index + 1}`
                        }
                        className="photo-swiper-image"
                      />
                      {imageId && (
                        <button
                          type="button"
                          className={`favorite-button swiper-favorite ${
                            image.favorite ? "is-active" : ""
                          }`}
                          onClick={() =>
                            toggleFavorite(imageId, image.favorite)
                          }
                          aria-label={
                            image.favorite
                              ? "Отстрани од омилени"
                              : "Додај во омилени"
                          }
                        >
                          {image.favorite ? (
                            <FavoriteIcon fontSize="small" />
                          ) : (
                            <FavoriteBorderIcon fontSize="small" />
                          )}
                        </button>
                      )}
                    </div>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </div>
        )}

        {renderGallery(images, "", true)}
      </>
    );
  };

  const renderFavoritePhotos = () => {
    if (favoritesLoading) {
      return <p className="photo-status">Се вчитуваат омилените фотографии...</p>;
    }

    if (favoritesError) {
      return <p className="photo-status error">{favoritesError}</p>;
    }
    return (
      <>
        {favorites.length > 0 && (
          <div className="album-download-bar">
            <button
              type="button"
              className="album-download-btn"
              onClick={downloadFavoritesZip}
              disabled={albumDownloading}
            >
              {albumDownloading ? "Се подготвува ZIP..." : "Преземи албум (ZIP)"}
            </button>
          </div>
        )}
        {renderGallery(favorites, "Немате омилени фотографии.")}
      </>
    );
  };

  const content =
    activeTab === 0 ? renderAllPhotos() : renderFavoritePhotos();

  return (
    <div className="photos-page">
      <SponsoredSlot placement="photos.top" />
      <div className="photos-header">
        <div className="photos-title-wrap">
          <h2 className="photos-title">Фотографии од свадбата</h2>
          <IconButton
            size="small"
            className="photos-info-button"
            aria-label="Инфо за QR код"
            onClick={(event) => setInfoAnchorEl(event.currentTarget)}
          >
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
          <Popover
            open={infoOpen}
            anchorEl={infoAnchorEl}
            onClose={() => setInfoAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
          >
            <Typography className="photos-info-popover">
              Поставете го QR кодот во дизајн на вашата свадба за гостите лесно
              да ги прикачуваат своите моменти и веднаш да ги споделат со вас.
            </Typography>
          </Popover>
        </div>
        {/* <Link
          className="photos-upload-link"
          to={
            weddingId
              ? `/upload-photos?wedding_id=${encodeURIComponent(weddingId)}`
              : "/upload-photos"
          }
        >
          Прикачете фотографии
        </Link> */}
        <button
          type="button"
          className="photos-upload-link"
          onClick={downloadQrPng}
          disabled={!weddingId || qrDownloading}
        >
          {qrDownloading ? "Се презема QR..." : "Преземи QR код (PNG)"}
        </button>
      </div>

      <Box className="photo-tabs">
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          variant="fullWidth"
        >
          <Tab label="Сите фотографии" />
          <Tab label="Мој албум" />
        </Tabs>
      </Box>

      <div className="photos-content">{content}</div>
    </div>
  );
}

// Helpers: ZIP download for favorites
async function ensureJSZip() {
  if (window.JSZip) return window.JSZip;
  await new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://cdn.jsdelivr.net/npm/jszip@3.10.1/dist/jszip.min.js';
    s.onload = resolve;
    s.onerror = () => reject(new Error('Не може да се вчита JSZip.'));
    document.head.appendChild(s);
  });
  if (!window.JSZip) throw new Error('JSZip не е достапен.');
  return window.JSZip;
}


export default Photos;
